import { useRef } from "react";
import { Tiles } from "@/components/motion/Tiles";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { Counter } from "@/components/motion/Counter";
import { Lines, Reveal, Rule } from "@/components/motion/Reveal";
import { FLEET } from "@/data/fleet";

const POINTS = [
  "Mezzi revisionati e con manutenzione documentata",
  "Consegna e ritiro in cantiere con nostri mezzi",
  "Piattaforme autocarrate guidabili con patente B",
  "Un unico referente: parli sempre con Sandro",
];

const METRICS = [
  { value: FLEET.reduce((a, v) => a + (v.units || 1), 0), suffix: "", label: "Mezzi in flotta" },
  { value: 24, suffix: " m", label: "Altezza massima di lavoro" },
  { value: 6, suffix: "/7", label: "Giorni operativi" },
  { value: 100, suffix: "%", label: "Mezzi verificati" },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ink py-24 text-white sm:py-32 grain" data-testid="about-section">
      <div className="absolute inset-0 blueprint opacity-50" />
      <div className="container-x relative grid gap-14 lg:grid-cols-12">
        <div className="relative lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden">
            <motion.img style={{ y: imgY }} src="/images/trasporto.jpg" alt="Trasporto mezzi DESP con l'Etna sullo sfondo" className="absolute inset-0 h-[120%] w-full -top-[10%] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
          </div>
          <Reveal delay={0.3} className="absolute -bottom-6 -right-2 border border-white/10 bg-blue-600 p-5 sm:right-[-1.5rem]">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/70">Sede operativa</p>
            <p className="display mt-1 text-3xl">Catania</p>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pl-8">
          <Reveal y={10}><p className="eyebrow text-blue-500 flex items-center gap-3"><Tiles />Chi siamo</p></Reveal>
          <Lines as="h2" lines={["Un'azienda di Catania,", "con i piedi in cantiere."]} className="display mt-4 text-4xl sm:text-5xl lg:text-6xl" />
          <Reveal delay={0.2} className="mt-7 space-y-4 text-base leading-relaxed text-white/65 sm:text-lg">
            <p>
              DESP nasce dall'esperienza diretta di Sandro Pace nei cantieri della Sicilia orientale. Conosciamo i tempi stretti, gli spazi ristretti dei centri storici e le salite dei paesi etnei: per questo la nostra flotta è fatta di mezzi compatti, affidabili e sempre pronti.
            </p>
            <p>
              Niente call center: chiami, parli con chi conosce i mezzi e ti consegna quello giusto. <span className="text-white">Dove serve, quando serve.</span>
            </p>
          </Reveal>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {POINTS.map((p, i) => (
              <Reveal key={p} delay={0.25 + i * 0.07} y={16}>
                <li className="flex items-start gap-3 text-sm text-white/80 sm:text-base">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center bg-blue-600"><Check className="h-3 w-3" strokeWidth={3} /></span>
                  {p}
                </li>
              </Reveal>
            ))}
          </ul>
          <Rule dark className="mt-12" />
          <div className="mt-8 grid grid-cols-2 gap-8 sm:grid-cols-4" data-testid="about-metrics">
            {METRICS.map((m, i) => (
              <Reveal key={m.label} delay={i * 0.08}>
                <Counter value={m.value} suffix={m.suffix} className="display block text-4xl text-white sm:text-5xl" testId={`metric-${i}`} />
                <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/45">{m.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
